import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api';
import { uc } from './unifiedContactsApi';
import { toast } from '../hooks/use-toast';
import { errorMessage } from './errors';
import { SEARCH_DEBOUNCE_MS } from './shared/useContactSearch';
import { X, Search, Users, Tag, Check, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

// Contacts come a page at a time; "Load more" asks for the next one.
const CONTACT_PAGE = 50;
// The status check takes this many addresses per call (the server's limit).
const STATUS_CHUNK = 500;
const ALL_TAGS = '__all__'; // Select items cannot have an empty value

// Why a send would skip an address (the server's reason → the badge).
const BLOCKED_LABELS = {
  unsubscribed: 'Unsubscribed',
  bounced: 'Bounced',
  complained: 'Marked as spam',
  suppressed: 'Suppressed',
  consent_revoked: 'Opted out',
  ghl_dnd: 'Do Not Disturb',
};
const blockedLabel = (why) => BLOCKED_LABELS[why] || 'Will be skipped';
const emailKey = (email) => String(email || '').trim().toLowerCase();
const plural = (n, one, many) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

const NO_AUDIENCE = { key: null, items: [], total: 0, noEmail: 0, tags: [], error: null };

// The contacts tab: the first page for a search + tag (debounced while typing), then more pages on
// demand. Answers for an older search are dropped. Contacts with no email never come back.
function useEmailAudience(enabled, query, tag) {
  const q = query.trim();
  const key = `${q}|${tag}`;
  const [res, setRes] = useState(NO_AUDIENCE);
  const [loadingMore, setLoadingMore] = useState(false);
  const latest = useRef(key);
  latest.current = key;

  useEffect(() => {
    if (!enabled) { setRes(NO_AUDIENCE); return undefined; }
    let live = true;
    const timer = setTimeout(() => {
      uc.emailAudience({ ...(q && { q }), ...(tag && { tag }), limit: CONTACT_PAGE, skip: 0 })
        .then((r) => {
          if (live) setRes({ key, items: r?.items || [], total: r?.total ?? 0, noEmail: r?.no_email ?? 0, tags: r?.tags || [], error: null });
        })
        .catch((error) => { if (live) setRes({ ...NO_AUDIENCE, key, error }); });
    }, q ? SEARCH_DEBOUNCE_MS : 0);
    return () => { live = false; clearTimeout(timer); };
  }, [enabled, q, tag, key]);

  const current = enabled && res.key === key;
  const loadMore = useCallback(() => {
    if (!current || loadingMore) return;
    setLoadingMore(true);
    uc.emailAudience({ ...(q && { q }), ...(tag && { tag }), limit: CONTACT_PAGE, skip: res.items.length })
      .then((r) => {
        if (latest.current !== key) return;
        setRes((prev) => {
          if (prev.key !== key) return prev;
          const seen = new Set(prev.items.map((i) => i.contact_id));
          return { ...prev, total: r?.total ?? prev.total, items: [...prev.items, ...(r?.items || []).filter((i) => !seen.has(i.contact_id))] };
        });
      })
      .catch((error) => toast({ title: 'Could not load more', description: errorMessage(error, 'Try again') }))
      .finally(() => setLoadingMore(false));
  }, [current, loadingMore, q, tag, key, res.items.length]);

  return {
    loading: enabled && !current,
    items: current ? res.items : NO_AUDIENCE.items,
    total: current ? res.total : 0,
    noEmail: current ? res.noEmail : 0,
    tags: current ? res.tags : NO_AUDIENCE.tags,
    error: current ? res.error : null,
    loadingMore,
    loadMore,
  };
}

/** CRM Recipient Picker — lists leads, and searches contacts on the server, lets
 *  the user filter by search / tag / type, and returns [{email, name}] on confirm.
 *  Addresses a send would skip (unsubscribed, bounced, opted out, Do Not Disturb)
 *  are shown with a badge and cannot be picked.
 *
 *  Props:
 *    open           — boolean to show/hide
 *    onClose        — close callback
 *    onConfirm(list)— called with [{email, name}] on confirm
 *    excludeEmails  — Set of emails already in the parent recipient list (dedupe visual)
 */
export default function CRMRecipientPicker({ open, onClose, onConfirm, excludeEmails = new Set() }) {
  const [source, setSource] = useState('leads');   // 'leads' | 'contacts'
  const [leads, setLeads] = useState([]);
  const [leadBlocks, setLeadBlocks] = useState({});  // lowercase address → why a send skips it
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [tag, setTag] = useState('');
  // address (lowercase) → {email, name}. Holds the pick itself, not just a key, so a
  // recipient stays picked after a new search drops it from the list on screen. One entry
  // per address, so a lead and a contact with the same email are one pick.
  const [picked, setPicked] = useState(new Map());
  const [matching, setMatching] = useState(false);  // "select all matching" in flight
  const [matchNote, setMatchNote] = useState('');
  const session = useRef(0);  // bumps each open/close, so a slow "select all" can't land in the next session

  // Ticket 188: contacts are searched on the server (the tenant has tens of thousands), so
  // only the leads are loaded up front and filtered in the browser.
  const audience = useEmailAudience(open && source === 'contacts', query, tag);

  useEffect(() => {
    session.current += 1;
    if (!open) return undefined;
    setLeadsLoading(true);
    setPicked(new Map());
    setQuery('');
    setTag('');
    setMatchNote('');
    setLeadBlocks({});
    let live = true;
    api.get('/leads')
      .then((l) => {
        if (!live) return undefined;
        const data = l.data || [];
        setLeads(data);
        const emails = [...new Set(data.map((r) => emailKey(r.email)).filter(Boolean))];
        const chunks = [];
        for (let i = 0; i < emails.length; i += STATUS_CHUNK) chunks.push(emails.slice(i, i + STATUS_CHUNK));
        return Promise.all(chunks.map((c) => uc.emailStatus(c)))
          .then((all) => { if (live) setLeadBlocks(Object.assign({}, ...all)); })
          .catch(() => toast({ title: 'Could not check unsubscribed leads', description: 'A send still skips anyone who opted out.' }));
      })
      .catch((e) => toast({ title: 'Failed to load CRM', description: errorMessage(e, 'Try again') }))
      .finally(() => { if (live) setLeadsLoading(false); });
    return () => { live = false; };
  }, [open]);

  const leadRows = useMemo(() => leads.map((r) => ({
    key: r.lead_id,
    name: r.name || '',
    email: r.email || '',
    company: r.company || '',
    tags: r.tags || [],
    blocked: leadBlocks[emailKey(r.email)] || null,
  })), [leads, leadBlocks]);

  const contactRows = useMemo(() => audience.items.map((r) => ({
    key: r.contact_id,
    name: r.name || '',
    email: r.email,
    company: r.company || '',
    tags: r.tags || [],
    blocked: r.blocked || null,
  })), [audience.items]);

  const rows = source === 'leads' ? leadRows : contactRows;
  const loading = source === 'leads' ? leadsLoading : audience.loading;
  const searchError = source === 'contacts' ? audience.error : null;

  // Contacts: the server's tag list for this search. Leads: the tags on the leads. The chosen
  // tag stays listed even when a new search no longer carries it.
  const allTags = useMemo(() => {
    const set = new Set();
    if (source === 'contacts') audience.tags.forEach((t) => set.add(t));
    else leadRows.forEach((r) => r.tags.forEach((t) => set.add(t)));
    if (tag) set.add(tag);
    return [...set].sort();
  }, [source, audience.tags, leadRows, tag]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // The server already matched contacts against the search and tag; only leads are filtered here.
    if (source !== 'leads') return rows.filter((r) => r.email);
    return rows.filter((r) => {
      if (!r.email) return false;                       // no email → can't send anyway
      if (tag && !r.tags.includes(tag)) return false;
      if (q && !`${r.name} ${r.email} ${r.company}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, query, tag, source]);

  const pickable = useMemo(() => filtered.filter((r) => !r.blocked), [filtered]);

  const toggle = (r) => {
    if (r.blocked) return;
    setPicked((prev) => {
      const next = new Map(prev);
      const id = emailKey(r.email);
      if (next.has(id)) next.delete(id); else next.set(id, { email: r.email, name: r.name });
      return next;
    });
  };

  const allShownSelected = pickable.length > 0 && pickable.every((r) => picked.has(emailKey(r.email)));
  const toggleAll = () => {
    setPicked((prev) => {
      const next = new Map(prev);
      if (allShownSelected) {
        // Deselect only the rows on screen (leave hidden picks intact)
        pickable.forEach((r) => next.delete(emailKey(r.email)));
      } else {
        pickable.forEach((r) => next.set(emailKey(r.email), { email: r.email, name: r.name }));
      }
      return next;
    });
  };

  // "Select all N matching": every sendable person for this search and tag, not just the loaded pages.
  const selectMatching = () => {
    setMatching(true);
    setMatchNote('');
    const q = query.trim();
    const mine = session.current;
    uc.emailAudienceAll({ ...(q && { q }), ...(tag && { tag }) })
      .then((r) => {
        if (session.current !== mine) return;
        const list = r?.recipients || [];
        setPicked((prev) => {
          const next = new Map(prev);
          list.forEach((p) => { if (!next.has(emailKey(p.email))) next.set(emailKey(p.email), { email: p.email, name: p.name || '' }); });
          return next;
        });
        const skipped = Object.entries(r?.skipped || {}).map(([why, n]) => `${n.toLocaleString('en-US')} ${blockedLabel(why).toLowerCase()}`);
        const parts = [`Selected ${plural(list.length, 'person', 'people')}.`];
        if (skipped.length) parts.push(`Left out: ${skipped.join(', ')}.`);
        if (r?.more) parts.push(`${r.cap.toLocaleString('en-US')} is the most at once. Narrow the search or send the rest in another batch.`);
        setMatchNote(parts.join(' '));
      })
      .catch((e) => { if (session.current === mine) toast({ title: 'Could not select everyone', description: errorMessage(e, 'Try again') }); })
      .finally(() => { if (session.current === mine) setMatching(false); });
  };

  const confirm = () => {
    const list = [...picked.values()];
    if (list.length === 0) {
      toast({ title: 'Pick at least one recipient' });
      return;
    }
    onConfirm?.(list);
    onClose?.();
  };

  if (!open) return null;

  const selectedCount = picked.size;
  const showContacts = source === 'contacts' && !audience.error && !audience.loading;
  const moreContacts = showContacts && audience.total > audience.items.length;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="crm-picker">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #6BA82E 0%, #8DC63F 100%)' }}>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/80 flex items-center gap-1"><Users size={11} /> CRM Recipient Picker</div>
            <div className="font-serif text-xl font-bold text-white">Pick from your leads and contacts</div>
          </div>
          <button onClick={onClose} data-testid="crm-picker-close" className="p-2 rounded-lg text-white/80 hover:bg-white/10 hover:text-white">
            <X size={16} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-3 border-b border-slate-100 space-y-2.5">
          <div className="flex gap-1">
            {[{ k: 'leads', l: `Leads (${leads.length})` }, { k: 'contacts', l: 'Contacts' }].map((t) => (
              <button
                key={t.k}
                onClick={() => { setSource(t.k); setTag(''); }}
                data-testid={`crm-source-${t.k}`}
                className="text-[11px] font-semibold px-3 py-1.5 rounded-lg border transition"
                style={source === t.k
                  ? { background: '#070B5F', color: 'white', borderColor: '#070B5F' }
                  : { background: 'white', color: '#070B5F', borderColor: '#e2e8f0' }}
              >{t.l}</button>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[180px]">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={source === 'leads' ? 'Search name, email or company…' : 'Search all contacts by name or email…'}
                className="pl-8 h-9 text-[13px]"
                data-testid="crm-search"
              />
            </div>
            <Select value={tag || ALL_TAGS} onValueChange={(v) => setTag(v === ALL_TAGS ? '' : v)}>
              <SelectTrigger className="h-9 w-[160px] text-[12px]" data-testid="crm-tag-filter" aria-label="Filter by tag">
                <SelectValue placeholder="All tags" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_TAGS}>All tags</SelectItem>
                {allTags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {showContacts && audience.noEmail > 0 && (
            <div className="text-[11px] text-slate-500" data-testid="crm-no-email">
              {plural(audience.noEmail, 'matching contact has', 'matching contacts have')} no email address and {audience.noEmail === 1 ? 'is' : 'are'} not listed.
            </div>
          )}
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-10 flex items-center justify-center gap-2 text-slate-500 text-sm"><Loader2 size={14} className="animate-spin" /> {source === 'leads' ? 'Loading CRM…' : 'Searching contacts…'}</div>
          ) : searchError ? (
            <div className="p-10 text-center text-sm text-red-600" data-testid="crm-picker-error">
              {errorMessage(searchError, 'Could not search contacts')}
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="crm-picker-empty">
              No {source} match your filters. {rows.length === 0 && !query.trim() && 'Your CRM is empty — add some contacts first.'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100 sticky top-0">
                <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="px-3 py-2.5 w-8">
                    <input
                      type="checkbox"
                      checked={allShownSelected}
                      disabled={pickable.length === 0}
                      onChange={toggleAll}
                      data-testid="crm-toggle-all"
                      aria-label="Select all shown"
                    />
                  </th>
                  <th className="text-left px-3 py-2.5 font-semibold">Name</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Email</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Tags</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((r) => {
                  const alreadyIn = excludeEmails.has(emailKey(r.email));
                  return (
                    <tr
                      key={r.key}
                      onClick={() => toggle(r)}
                      className={r.blocked ? 'bg-slate-50/60 text-slate-400' : 'hover:bg-slate-50/60 cursor-pointer'}
                      data-testid={`crm-row-${r.key}`}
                    >
                      <td className="px-3 py-2.5">
                        <input
                          type="checkbox"
                          checked={picked.has(emailKey(r.email))}
                          disabled={!!r.blocked}
                          onChange={() => toggle(r)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Pick ${r.name || r.email}`}
                        />
                      </td>
                      <td className="px-3 py-2.5">
                        <div className={`font-semibold text-[13px] ${r.blocked ? 'text-slate-400' : 'text-slate-800'}`}>{r.name || '—'}</div>
                        {r.company && <div className="text-[10px] text-slate-400">{r.company}</div>}
                      </td>
                      <td className={`px-3 py-2.5 font-mono text-[12px] ${r.blocked ? 'text-slate-400' : 'text-slate-700'}`}>
                        {r.email}
                        {r.blocked && (
                          <span className="ml-1.5 text-[10px] font-sans font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700" data-testid={`crm-blocked-${r.key}`}>
                            {blockedLabel(r.blocked)}
                          </span>
                        )}
                        {alreadyIn && <span className="ml-1.5 text-[10px] text-emerald-600 font-sans font-semibold"><Check size={10} className="inline -mt-0.5" /> in list</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-wrap gap-1">
                          {r.tags.slice(0, 4).map((t) => (
                            <span key={t} className="text-[10px] px-1.5 py-0.5 rounded font-semibold" style={{ background: '#070B5F10', color: '#070B5F' }}>
                              <Tag size={8} className="inline -mt-0.5 mr-0.5" />{t}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {showContacts && audience.total > 0 && (
            <div className="px-6 py-3 flex flex-wrap items-center justify-center gap-3 text-xs text-slate-500" data-testid="crm-picker-more">
              <span>Showing {audience.items.length.toLocaleString('en-US')} of {audience.total.toLocaleString('en-US')} contacts with an email.</span>
              {moreContacts && (
                <button onClick={audience.loadMore} disabled={audience.loadingMore} data-testid="crm-load-more" className="font-semibold px-3 py-1 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-50">
                  {audience.loadingMore ? 'Loading…' : 'Load more'}
                </button>
              )}
              <button onClick={selectMatching} disabled={matching} data-testid="crm-select-matching" className="font-semibold px-3 py-1 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-50" style={{ color: '#070B5F' }}>
                {matching ? 'Selecting…' : `Select all ${audience.total.toLocaleString('en-US')} matching`}
              </button>
            </div>
          )}
          {matchNote && (
            <div className="px-6 pb-3 text-center text-xs text-slate-600" data-testid="crm-match-note">{matchNote}</div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between gap-3">
          <div className="text-[12px] text-slate-600" data-testid="crm-picker-count">
            <strong className="font-semibold text-slate-800">{selectedCount}</strong> selected · {filtered.length} shown
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-[13px] font-semibold px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-100" data-testid="crm-picker-cancel">Cancel</button>
            <button
              onClick={confirm}
              disabled={selectedCount === 0}
              data-testid="crm-picker-confirm"
              className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}
            >
              Add {selectedCount > 0 ? `${selectedCount} ` : ''}Recipient{selectedCount === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
